/*
 * busca-estrita.js — Kohde (Dawn 15.2)
 * Faz a busca procurar só no TÍTULO do produto, com variação de gênero/plural
 * (preto ↔ preta, vestidos → vestido). Se a busca estrita não achar nada,
 * cai automaticamente na busca normal do Shopify (mantém tolerância a erro de digitação).
 * Não altera nenhum arquivo do Dawn: só estende o <predictive-search> e o submit dos forms.
 */
(() => {
  const STOPWORDS = new Set(['de', 'da', 'do', 'das', 'dos', 'com', 'c/', 'e', 'em', 'para', 'a', 'o']);

  const swapGender = (w) => (/o$/i.test(w) ? w.slice(0, -1) + 'a' : /a$/i.test(w) ? w.slice(0, -1) + 'o' : null);

  const formsOf = (w) => {
    const forms = new Set([w]);
    const g = swapGender(w);
    if (g) forms.add(g);
    if (/s$/i.test(w) && w.length > 4) {
      const sing = w.slice(0, -1); // vestidos → vestido
      forms.add(sing);
      const gs = swapGender(sing);
      if (gs) forms.add(gs);
    }
    return [...forms];
  };

  // "saia preta" → "(title:saia OR title:saio) (title:preta OR title:preto)"
  const toStrict = (term) => {
    const t = (term || '').trim();
    if (!t || /[:"*()]/.test(t)) return null; // vazio ou busca avançada: não mexe
    const words = t.toLowerCase().split(/\s+/).filter((w) => w && !STOPWORDS.has(w) && w.length > 1);
    if (!words.length) return null;
    return words
      .map((w) => {
        const f = formsOf(w);
        return f.length > 1 ? `(${f.map((x) => `title:${x}`).join(' OR ')})` : `title:${w}`;
      })
      .join(' ');
  };

  // "(title:saia OR title:saio) (title:preta OR title:preto)" → "saia preta"
  const fromStrict = (q) => {
    if (!q || !q.includes('title:')) return q;
    const tokens = q.replace(/[()]/g, ' ').split(/\s+/).filter(Boolean);
    const out = [];
    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i] === 'OR') { i++; continue; } // pula o OR e a variação seguinte
      out.push(tokens[i].replace(/^title:/, ''));
    }
    return out.join(' ');
  };

  const escapeHtml = (s) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  /* ---------- 1. Dropdown (predictive search) ---------- */
  customElements.whenDefined('predictive-search').then(() => {
    const proto = customElements.get('predictive-search').prototype;
    const original = proto.getSearchResults;

    // Correção de bug do Dawn: o original usa innerText (vem em MAIÚSCULAS por causa do CSS),
    // o match dá null e quebra o onChange — o dropdown fica vazio a partir do 2º termo digitado.
    proto.updateSearchForTerm = function (previousTerm, newTerm) {
      const el = this.querySelector('[data-predictive-search-search-for-text]');
      if (!el || !previousTerm) return;
      const text = el.textContent;
      const i = text.toLowerCase().lastIndexOf(previousTerm.toLowerCase());
      if (i === -1) return;
      el.textContent = text.slice(0, i) + newTerm + text.slice(i + previousTerm.length);
    };

    proto.getSearchResults = function (searchTerm) {
      const strict = toStrict(searchTerm);
      const queryKey = searchTerm.replace(' ', '-').toLowerCase();
      if (!strict || this.cachedResults[queryKey]) return original.call(this, searchTerm);

      this.setLiveRegionLoadingState();
      const get = (q) =>
        fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(q)}&section_id=predictive-search`, {
          signal: this.abortController.signal,
        }).then((r) => {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        });

      get(strict)
        .then((text) => (/predictive-search-results-products-list/.test(text) ? text : get(searchTerm)))
        .then((text) => {
          // o Shopify devolve o termo pesquisado no markup ("Pesquisar por ..."): troca o estrito pelo digitado
          const markup = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search')
            .innerHTML.split(escapeHtml(strict)).join(escapeHtml(searchTerm))
            .split(strict).join(escapeHtml(searchTerm));
          this.allPredictiveSearchInstances.forEach((i) => (i.cachedResults[queryKey] = markup));
          this.renderSearchResults(markup);
        })
        .catch((error) => {
          if (error?.code !== 20) this.close();
        });
    };
  });

  /* ---------- 2. Enviar a busca (Enter / lupa) ---------- */
  document.addEventListener('submit', (e) => {
    if (e.defaultPrevented) return; // Dawn já tratou (ex.: item do dropdown selecionado)
    const form = e.target;
    if (!form.matches || !form.matches('form[action*="/search"]')) return;
    const input = form.querySelector('input[name="q"]:not([type="hidden"])');
    if (!input) return;
    const strict = toStrict(input.value);
    if (!strict) return;
    e.preventDefault();
    const params = new URLSearchParams(new FormData(form));
    const go = (q) => {
      params.set('q', q);
      window.location.href = `${form.getAttribute('action')}?${params.toString()}`;
    };
    // checa antes se a busca estrita acha algo, pra não mostrar uma página vazia
    fetch(`${routes.predictive_search_url}.json?q=${encodeURIComponent(strict)}&resources[type]=product&resources[limit]=1`)
      .then((r) => r.json())
      .then((d) => go(d?.resources?.results?.products?.length ? strict : input.value))
      .catch(() => go(input.value));
  });

  /* ---------- 3. Página de resultados: fallback + texto limpo ---------- */
  if (!/\/search\/?$/.test(window.location.pathname)) return;
  const params = new URLSearchParams(window.location.search);
  const q = params.get('q');
  if (!q || !q.includes('title:')) return;
  const clean = fromStrict(q);

  const run = () => {
    const main = document.querySelector('main') || document.body;
    const empty =
      document.querySelector('.template-search--empty') || !main.querySelector('a[href*="/products/"]');

    const hasFilters = [...params.keys()].some((k) => k.startsWith('filter.'));
    if (empty && !hasFilters) {
      params.set('q', clean); // nada no título → busca normal do Shopify
      window.location.replace(`${window.location.pathname}?${params.toString()}`);
      return;
    }

    const fix = () => {
      document.querySelectorAll('input[name="q"]').forEach((i) => {
        if (i.type !== 'hidden' && i.value.includes('title:')) i.value = clean;
      });
      if (document.title.includes('title:')) document.title = document.title.split(q).join(clean);
      const walker = document.createTreeWalker(main, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const n = walker.currentNode;
        if (n.textContent.includes(q)) n.textContent = n.textContent.split(q).join(clean);
      }
    };
    fix();
    new MutationObserver(fix).observe(main, { childList: true, subtree: true }); // filtros via AJAX
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();